import * as React from 'react';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faBell as farBell } from '@fortawesome/pro-regular-svg-icons/faBell';
import { faBell as fasBell } from '@fortawesome/pro-solid-svg-icons/faBell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';
import CountBadge from '@liveauctioneers/caterwaul-components/lib/CountBadge/CountBadge';
import EnableNotificationPrompt from './EnableNotificationPrompt';
import EnableNotifications from './EnableNotifications';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import Notification from './Notification';
import notificationPermissions from '@/enums/notificationPermissions';
import Popover from '@liveauctioneers/caterwaul-components/lib/Popover/Popover';
import styled, { css } from 'styled-components';

type DropDownMenuProps = {
    forceEnableNotificationsBanner: Function;
    markNotificationsDeleted: Function;
    markNotificationsRead: Function;
    mobile: boolean;
    notificationCount: number;
    notifications: any[];
    openMobileDownloadModal: Function;
    permission: string;
    requestBrowserPermission: Function;
    setPermission: Function;
    trackView: Function;
};

type DropDownMenuState = {
    hasTracked: boolean;
    show: boolean;
};

export default class NotificationDropDownMenu extends React.Component<DropDownMenuProps, DropDownMenuState> {
    static defaultProps = {
        notificationCount: 0,
        notifications: [],
        permission: notificationPermissions.DEFAULT,
    };

    state = { hasTracked: false, show: false };

    shouldComponentUpdate = (nextProps: DropDownMenuProps, nextState: DropDownMenuState) =>
        !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

    targetRef: HTMLAnchorElement | null | undefined;

    open = () => {
        this.setState({ show: true });
        if (!this.state.hasTracked) {
            this.props.trackView();
            this.setState({ hasTracked: true });
        }
    };

    closeAndMarkRead = () => {
        if (this.props.notificationCount > 0) {
            this.markAllNotificationsAsRead();
        }
        this.close();
    };

    close = () => {
        this.setState({ show: false });
    };

    toggle = () => {
        if (this.state.show) {
            this.closeAndMarkRead();
        } else {
            this.open();
        }
    };

    markAllNotificationsAsRead = () => {
        const notificationIds = this.props.notifications.map((n) => n.id);
        this.props.markNotificationsRead(notificationIds);
    };

    markAllNotificationsAsDeleted = async () => {
        const notificationIds = this.props.notifications.map((n) => n.id);
        this.props.markNotificationsDeleted(notificationIds);
    };

    openModal = () => {
        this.props.openMobileDownloadModal();
    };

    render() {
        const {
            forceEnableNotificationsBanner,
            markNotificationsDeleted,
            markNotificationsRead,
            mobile,
            notificationCount,
            notifications,
            permission,
            requestBrowserPermission,
            setPermission,
        } = this.props;
        const hasNewNotifications = notificationCount > 0;
        const hasNotifications = notifications.length > 0;
        return (
            <StyledOverlayPopover
                hover={false}
                placement="bottom"
                trigger={
                    <ButtonStyles>
                        <a onClick={this.toggle} ref={(ref) => (this.targetRef = ref)}>
                            <StyledCountBadge count={notificationCount} />
                            <StyledMenuItemVariant>
                                <StyledExpanderIcon icon={faCaretDown} />
                                <StyledHasNewNotifications
                                    $hasNotifications={Boolean(hasNewNotifications)}
                                    icon={hasNewNotifications ? fasBell : farBell}
                                />
                                <StyledMenuText>
                                    <FormattedMessage id="userMenu.notifications" />
                                </StyledMenuText>
                            </StyledMenuItemVariant>
                        </a>
                    </ButtonStyles>
                }
            >
                <StyledPopoverDiv id="popover-trigger-click-root-close" onMouseLeave={this.closeAndMarkRead}>
                    <StyledDeleteAllSpan>
                        <StyledMarkAllReadLink
                            $hasNotifications={Boolean(hasNotifications)}
                            onClick={this.markAllNotificationsAsDeleted}
                        >
                            <FormattedMessage id="notifications.delete_all" />
                        </StyledMarkAllReadLink>
                    </StyledDeleteAllSpan>
                    <hr />
                    <EnableNotificationPrompt
                        forceEnableNotificationsBanner={forceEnableNotificationsBanner}
                        hasNotifications={hasNotifications}
                        permission={permission}
                        requestBrowserPermission={requestBrowserPermission}
                    />
                    {hasNotifications ? (
                        <StyledNotificationsDiv>
                            {notifications.map((n, idx) => {
                                return (
                                    <Notification
                                        id={n.id}
                                        imageUrl={n.imageUrl}
                                        isNew={idx < notificationCount}
                                        key={n.id}
                                        link={n.link}
                                        markNotificationsDeleted={markNotificationsDeleted}
                                        markNotificationsRead={markNotificationsRead}
                                        message={n.message}
                                        messageLevel={n.messageLevel}
                                        timestamp={n.timestamp}
                                        title={n.title}
                                    />
                                );
                            })}
                        </StyledNotificationsDiv>
                    ) : (
                        <div>
                            <StyledEmptyStateDiv>
                                <FormattedMessage id="notifications.no_new_notifications" />
                            </StyledEmptyStateDiv>
                        </div>
                    )}
                    {mobile ? (
                        <StyledDownloadLinkDiv>
                            <FormattedMessage id="notifications.get_notifications_phone" />
                            <br />
                            <Link onClick={this.openModal}>
                                <FormattedMessage id="notifications.download_app" />
                            </Link>
                        </StyledDownloadLinkDiv>
                    ) : (
                        <StyledDownloadLinkDiv>
                            <EnableNotifications
                                forceEnableNotificationsBanner={forceEnableNotificationsBanner}
                                permission={permission}
                                requestBrowserPermission={requestBrowserPermission}
                                setPermission={setPermission}
                            />
                        </StyledDownloadLinkDiv>
                    )}
                </StyledPopoverDiv>
            </StyledOverlayPopover>
        );
    }
}

const StyledOverlayPopover = styled(Popover)`
    min-width: 260px;

    hr {
        padding: 0;
        border: none;
        border-top: 1px solid ${({ theme }) => theme.colors.grey400};
        border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
    }
`;

const ButtonStyles = styled.div`
    cursor: pointer;
    position: relative;

    a {
        text-decoration: none;
    }
`;

const StyledCountBadge = styled(CountBadge)`
    position: absolute;
    left: 54px;
    top: 1px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        left: 23px;
    }
`;

const StyledMenuItemVariant = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
    width: 109px;
    min-width: 109px;
    color: ${({ theme }) => theme.colors.grey100};
    padding: 4px 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        width: 44px;
        min-width: 44px;
    }
    &:hover {
        color: ${({ theme }) => theme.colors.blue200};

        svg {
            color: ${({ theme }) => theme.colors.blue200};
        }
    }
`;

const StyledExpanderIcon = styled(FontAwesomeIcon)`
    position: absolute;
    right: 8px;
    top: -2px;
    @media (max-width: 1199px) and (min-width: 320px) {
        display: none;
    }
`;

const StyledHasNewNotifications = styled(FontAwesomeIcon)<{
    $hasNotifications?: boolean;
}>`
    ${({ $hasNotifications }) => {
        if ($hasNotifications) {
            return css`
                font-size: 22px;
                color: ${({ theme }) => theme.colors.blue100};
            `;
        } else {
            return css`
                color: ${({ theme }) => theme.colors.grey100};
                fill: ${({ theme }) => theme.colors.grey100};
                font-size: 22px;

                &:hover {
                    color: ${({ theme }) => theme.colors.blue200};
                    fill: ${({ theme }) => theme.colors.blue200};
                }
            `;
        }
    }}
`;

const StyledMenuText = styled.span`
    line-height: 12px;
    font-size: 12px;
    padding-top: 5px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        display: none;
    }
`;

const StyledPopoverDiv = styled.div`
    min-width: 350px;
    padding: 10px 0;
    width: 350px;
    border-radius: 10px;
    background-color: ${({ theme }) => theme.colors.white};

    hr {
        margin: 0;
    }
`;

const StyledDeleteAllSpan = styled.span`
    margin: -2px 0 4px;
    display: flex;
    justify-content: space-between;
`;

const StyledMarkAllReadLink = styled(Link)<{
    $hasNotifications?: boolean;
}>`
    ${({ $hasNotifications }) => {
        if ($hasNotifications) {
            return css`
                color: ${({ theme }) => theme.colors.grey100};
                font-family: ${({ theme }) => theme.typography.fontPrimary};
                font-size: 12px;
                letter-spacing: 0.5px;
                margin-left: auto;
                margin-right: 13px;
                text-decoration: none;

                &:hover {
                    color: ${({ theme }) => theme.colors.blue200};
                    text-decoration: none;
                }
            `;
        } else {
            return css`
                color: ${({ theme }) => theme.colors.grey300};
                cursor: default;
                font-family: ${({ theme }) => theme.typography.fontPrimary}
                font-size: 12px;
                letter-spacing: 0.5px;
                margin-left: auto;
                margin-right: 3px;
                text-decoration: none;
                &:hover {
                    color: ${({ theme }) => theme.colors.grey300};
                    cursor: default;
                    font-family: ${({ theme }) => theme.typography.fontPrimary}
                    font-size: 12px;
                    letter-spacing: 0.5px;
                    margin-left: auto;
                    margin-right: 3px;
                    text-decoration: none;
                }
            `;
        }
    }}
`;

const StyledNotificationsDiv = styled.div`
    max-height: 405px;
    overflow-x: hidden;
    overflow-y: scroll;
`;

const StyledEmptyStateDiv = styled.div`
    text-align: center;
    color: ${({ theme }) => theme.colors.grey100};
    font-family: ${({ theme }) => theme.typography.fontSecondary};
    font-size: 14px;
    letter-spacing: 0.5px;
    line-height: 1.5;
    margin: 22px 0;
`;

const StyledDownloadLinkDiv = styled.div`
    color: ${({ theme }) => theme.colors.black};
    text-align: center;
    font-family: ${({ theme }) => theme.typography.fontPrimary};
    font-size: 14px;
    letter-spacing: 0.5px;
    line-height: 20px;
    margin: 1px 0 5px;
    padding-top: 10px;
    border-top: 1px solid ${({ theme }) => theme.colors.grey400};
`;
